'use client';

import { uiConfig } from '@web/constants';
import { StatusVariant } from '@web/types';

import { LinearProgress } from '../LinearProgress';

import styles from './MainButton.module.scss';

type ButtonVariant = 'primary' | StatusVariant;

type ButtonProps = {
  children: React.ReactNode;
  ariaLabel?: string;
  type?: 'button' | 'submit' | 'reset';
  /**
   * * Optional form id to associate this button with a <form /> element
   * * outside of its DOM tree (native HTML `form` attribute).
   */
  form?: string;
  variant?: ButtonVariant;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
};

export function MainButton({
  children,
  ariaLabel,
  type = 'button',
  form = undefined,
  variant = 'primary',
  disabled = false,
  isLoading = false,
  onClick = () => {},
  style = {},
}: ButtonProps) {
  const backgroundColor =
    variant === 'primary'
      ? 'var(--nn-color-primary)'
      : uiConfig.statusVariants[variant as StatusVariant];

  const textColor =
    variant === 'warning' ? 'var(--nn-black)' : 'var(--nn-white)';

  return (
    <button
      tabIndex={0}
      type={type}
      form={form}
      disabled={disabled || isLoading}
      className={`button-base ${styles.mainButton}`}
      data-variant={variant}
      aria-label={ariaLabel}
      aria-busy={isLoading}
      onClick={onClick}
      style={{
        backgroundColor: backgroundColor,
        color: textColor,
        ...style,
      }}
    >
      {isLoading ? <LinearProgress>{children}</LinearProgress> : children}
    </button>
  );
}
